import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { POSManagerOperator } from 'src/app/models/pos-manager.model';
import {
  CreatePOSOperator,
  GetPOSOperators,
  CreatePOSOperatorSuccess,
  UpdatePOSOperator,
  UpdatePOSOperatorSuccess,
  DeletePOSOperator,
  DeletePOSOperatorSuccess
} from './pos-operator.action';
import { PosOperatorService } from 'src/app/services/pos-manager/pos-operator/pos-operator.service';
import { catchError, from, switchMap, tap } from 'rxjs';
import { ExceptionError } from '../../error/error.action';
import { ModalService } from 'src/app/services/modal/modal.service';
import { ToastController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';

export interface PosManagerOperatorStateModel {
  operators: POSManagerOperator[];
  isLoading: boolean;
}

@State<PosManagerOperatorStateModel>({
  name: 'operators',
  defaults: {
    isLoading: false,
    operators: []
  }
})
@Injectable({ providedIn: 'root' })
export class PosManagerOperatorState {
  constructor(
    private operatorService: PosOperatorService,
    private modalService: ModalService,
    private toastController: ToastController,
    private translocoService: TranslocoService
  ) {}

  @Action(GetPOSOperators)
  getOperators(ctx: StateContext<PosManagerOperatorStateModel>) {
    ctx.patchState({ isLoading: true });
    return this.operatorService.getOperators().pipe(
      tap((operators) => {
        //TODO: sort items before storing them
        ctx.patchState({ operators, isLoading: false });
      }),
      catchError(() =>
        ctx.dispatch(
          new ExceptionError(
            this.translocoService.translate('errors.errorFetchingOperators')
          )
        )
      )
    );
  }

  @Action(CreatePOSOperator)
  createOperators(
    ctx: StateContext<PosManagerOperatorStateModel>,
    { newOperator }: CreatePOSOperator
  ) {
    return from(this.modalService.showLoadingModal('Saving...')).pipe(
      switchMap((loadingModal) =>
        this.operatorService.createOperator(newOperator).pipe(
          tap((operator) => {
            void (async () => {
              const toast = await this.toastController.create({
                color: 'primary',
                duration: 2000,
                message: 'Operator saved successfully.',
                position: 'top'
              });
              await toast.present();
              this.modalService.closeLoadingModal(loadingModal.id);
              ctx.dispatch([
                new CreatePOSOperatorSuccess(operator),
                new GetPOSOperators()
              ]);
            })().catch((err) => {
              console.error('Error in operator save flow:', err);
            });
          }),
          catchError(async (err) => {
            await this.modalService.closeLoadingModal(loadingModal.id);
            let message = 'There was a problem while creating operator.';
            if (err?.error?.error?.length) {
              message = err?.error?.error[0];
            }
            return ctx.dispatch(new ExceptionError(message));
          })
        )
      )
    );
  }

  @Action(UpdatePOSOperator)
  updateOperators(
    ctx: StateContext<PosManagerOperatorStateModel>,
    { operator }: UpdatePOSOperator
  ) {
    return from(this.modalService.showLoadingModal('Saving...')).pipe(
      switchMap((loadingModal) =>
        this.operatorService.updateOperator(operator).pipe(
          tap(async () => {
            const toast = await this.toastController.create({
              color: 'primary',
              duration: 2000,
              message: 'Operator saved successfully.',
              position: 'top'
            });
            await toast.present();
            this.modalService.closeLoadingModal(loadingModal.id);
            ctx.dispatch([
              new UpdatePOSOperatorSuccess(),
              new GetPOSOperators()
            ]);
          }),
          catchError(async (err) => {
            await this.modalService.closeLoadingModal(loadingModal.id);
            let message = 'There was a problem while updating operator.';
            if (err?.error?.error?.length) {
              message = err?.error?.error[0];
            }
            return ctx.dispatch(new ExceptionError(message));
          })
        )
      )
    );
  }

  @Action(DeletePOSOperator)
  /**
   * Deletes an operator by ID, shows a loading modal, and dispatches success or error actions.
   * @param ctx StateContext<PosManagerOperatorStateModel> - The state context for operator management.
   * @param id number - The ID of the operator to delete.
   * @returns Observable<any> An observable representing the delete operation.
   */
  deleteOperators(
    ctx: StateContext<PosManagerOperatorStateModel>,
    { id }: DeletePOSOperator
  ) {
    return from(this.modalService.showLoadingModal('Deleting...')).pipe(
      switchMap((loadingModal) =>
        this.operatorService.deleteOperator(id).pipe(
          tap(async () => {
            const toast = await this.toastController.create({
              color: 'primary',
              duration: 2000,
              message: 'Operator deleted successfully.',
              position: 'top'
            });
            await toast.present();
            this.modalService.closeLoadingModal(loadingModal.id);
            ctx.dispatch([
              new DeletePOSOperatorSuccess(),
              new GetPOSOperators()
            ]);
          }),
          catchError(async (err) => {
            await this.modalService.closeLoadingModal(loadingModal.id);
            let message = 'There was a problem while deleting operator.';
            if (err?.error?.error?.length) {
              message = err?.error?.error[0];
            }
            return ctx.dispatch(new ExceptionError(message));
          })
        )
      )
    );
  }
}
